// Vitals quick-entry modal — Phase 3 (2026-10-03).
//
// Opened from the admissions board. Writes to the SAME encounters
// /observations endpoint that the EncounterDetail vitals row uses — no
// new API, just a focused UI. The server-side observation insert bumps
// last_vitals_observed_at + next_vitals_due_at on the open admission
// (see modules/db/admissions.touchAdmissionOnObservationInsert) so the
// board card freshness and overdue chip update without a hand-rolled
// write path.

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiError, encountersApi } from '../lib/api';
import { ModalShell } from './AdmitDialog';

interface Props {
    encounterId: string;
    admissionId: string;
    onClose: () => void;
    onRecorded: () => void;
}

interface Row {
    code: string;
    label: string;
    unit: string | null;
    placeholder?: string;
    min?: number;
    max?: number;
}

const ROWS: Row[] = [
    { code: 'systolic_bp',  label: 'Systolic BP',  unit: 'mmHg', placeholder: '120', min: 50,  max: 260 },
    { code: 'diastolic_bp', label: 'Diastolic BP', unit: 'mmHg', placeholder: '80',  min: 30,  max: 160 },
    { code: 'pulse_bpm',    label: 'Pulse',        unit: 'bpm',  placeholder: '75',  min: 20,  max: 220 },
    { code: 'temp_c',       label: 'Temperature',  unit: '°C',   placeholder: '36.8', min: 30, max: 43 },
    { code: 'spo2',         label: 'SpO₂',         unit: '%',    placeholder: '98',  min: 50,  max: 100 },
    { code: 'respiratory_rate', label: 'Resp. rate', unit: '/min', placeholder: '16', min: 4, max: 60 },
];

export function VitalsQuickEntryModal({
    encounterId,
    admissionId,
    onClose,
    onRecorded,
}: Props) {
    const qc = useQueryClient();
    const [values, setValues] = useState<Record<string, string>>({});
    const [error, setError] = useState<string | null>(null);

    const mut = useMutation({
        mutationFn: async (entries: Array<{ code: string; value: number; unit: string | null }>) => {
            // Sequential — each POST runs its own authz + consent check and
            // the per-observation side effect (touchAdmission) is idempotent
            // anyway. Volume per modal submit is 1–6 writes.
            for (const e of entries) {
                await encountersApi.addObservation(encounterId, {
                    code: e.code,
                    valueNumeric: e.value,
                    unit: e.unit,
                });
            }
        },
        onSuccess: async () => {
            await Promise.all([
                qc.invalidateQueries({ queryKey: ['admissions'] }),
                qc.invalidateQueries({ queryKey: ['admission', admissionId] }),
                qc.invalidateQueries({ queryKey: ['encounter', encounterId] }),
            ]);
            setError(null);
            onRecorded();
        },
        onError: (err) => {
            setError(err instanceof ApiError ? err.message : 'Could not record vitals.');
        },
    });

    function submit() {
        setError(null);
        const entries: Array<{ code: string; value: number; unit: string | null }> = [];
        for (const r of ROWS) {
            const raw = (values[r.code] ?? '').trim();
            if (raw === '') continue;
            const n = Number(raw);
            if (!Number.isFinite(n)) {
                setError(`${r.label} must be a number.`);
                return;
            }
            if (r.min != null && n < r.min) {
                setError(`${r.label} must be ≥ ${r.min}.`);
                return;
            }
            if (r.max != null && n > r.max) {
                setError(`${r.label} must be ≤ ${r.max}.`);
                return;
            }
            entries.push({ code: r.code, value: n, unit: r.unit });
        }
        if (entries.length === 0) {
            setError('Enter at least one value.');
            return;
        }
        mut.mutate(entries);
    }

    return (
        <ModalShell title="Record vitals" onClose={onClose}>
            <div className="grid grid-cols-2 gap-3">
                {ROWS.map((r) => (
                    <div key={r.code}>
                        <label className="pm-label">
                            {r.label}{r.unit ? ` (${r.unit})` : ''}
                        </label>
                        <input
                            className="pm-input"
                            type="number"
                            step="any"
                            placeholder={r.placeholder}
                            value={values[r.code] ?? ''}
                            onChange={(e) => setValues((v) => ({ ...v, [r.code]: e.target.value }))}
                            disabled={mut.isPending}
                        />
                    </div>
                ))}
            </div>
            {error && <div className="pm-error mt-3">{error}</div>}
            <div className="mt-4 flex justify-end gap-2">
                <button
                    type="button"
                    className="pm-btn-ghost"
                    onClick={onClose}
                    disabled={mut.isPending}
                >
                    Cancel
                </button>
                <button
                    type="button"
                    className="pm-btn-primary"
                    onClick={submit}
                    disabled={mut.isPending}
                >
                    {mut.isPending ? 'Saving…' : 'Save vitals'}
                </button>
            </div>
        </ModalShell>
    );
}
